import {
  Typography,
  IconButton,
  Divider,
  TextField,
  MenuItem,
  Box,
  SvgIcon,
  Checkbox,
  FormControlLabel,
  Chip,
  Autocomplete,
} from '@mui/material';
import React, { useEffect, useRef, useState } from 'react';
import { Close as CloseIcon, UploadFile as UploadFileIcon } from '@mui/icons-material';
import { v4 as uuidv4 } from 'uuid';
import { useSelector } from 'react-redux';
import CustomButton from '../../../../../components/General/CustomButton';
import useStyles from '../../styles';
import { DeviceInfo, ErrorState } from './types';
import { ReduxState } from '../../../../../types';
import apiClient from '../../../../../requests/api/apiClient';
import { sensorTypes } from '../../types';

const AddDevicesFlowSecondStep = ({
  deviceInfo,
  setDeviceInfo,
  isSensor,
  handleCloseModal,
  handleStep,
  error,
  setError,
  files,
  setFiles,
}: {
  deviceInfo: DeviceInfo,
  setDeviceInfo: React.Dispatch<React.SetStateAction<DeviceInfo>>,
  isSensor: boolean,
  handleCloseModal: VoidFunction,
  handleStep: (step: 'firstStep' | 'secondStep' | 'thirdStep') => void,
  error: ErrorState,
  setError: React.Dispatch<React.SetStateAction<ErrorState>>,
  files: Array<{ id: string, file: File }>,
  setFiles: React.Dispatch<React.SetStateAction<Array<{ id: string, file: File }>>>,
}) => {
  const classes = useStyles();
  const fileInputRef = useRef<HTMLInputElement>(null);
  const { selectedFarm } = useSelector((state: ReduxState) => state.accountFarm);
  // const { allFarmDevices } = useSelector((state: ReduxState) => state.farmSector);
  const [nodeModels, setNodeModels] = useState<{ models: string[], brands: string[] }>(
    { models: [], brands: [] },
  );
  const [sensorModels, setSensorModels] = useState<{ models: string[], brands: string[] }>(
    { models: [], brands: [] },
  );
  const [nodes, setNodes] = useState<any>([]);

  useEffect(() => {
    const getDeviceModels = async () => {
      const models = await apiClient.devices.softlandGetDeviceModelsList();
      setNodeModels({
        models: models.data?.nodeModels ?? [], brands: models.data?.nodeBrands ?? [],
      });
      setSensorModels({
        models: models.data?.sensorModels ?? [], brands: models.data?.sensorBrands ?? [],
      });
    };
    getDeviceModels();
  }, []);

  useEffect(() => {
    const getNodesByFarmId = async () => {
      if (selectedFarm?.id) {
        const response = await apiClient.nodes.nodesByFarmId(selectedFarm?.id);
        setNodes(response?.data?.nodes ?? []);
      }
    };
    getNodesByFarmId();
  }, [selectedFarm]);

  const handleValueChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement> | string,
    source: string,
    overrideValue?: boolean,
  ) => {
    let valueToSet: string | boolean;
    if (typeof e === 'object') {
      if (e.target.type === 'checkbox') {
        valueToSet = (e.target as HTMLInputElement).checked;
      } else {
        valueToSet = overrideValue !== undefined ? overrideValue : e.target.value;
      }
    } else {
      valueToSet = e;
    }

    setDeviceInfo((prevState) => ({
      ...prevState,
      [source]: valueToSet,
    }));
    setError((prevState) => ({ ...prevState, [source]: '' }));
  };

  const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    if (event.target.files) {
      const filesArray = Array.from(event.target.files).map((file) => ({
        id: uuidv4(),
        file,
      }));
      setFiles((prevState) => ([...prevState, ...filesArray]));
    }
  };

  const handleDeleteFile = (fileId: string) => {
    const remainingFiles = files.filter((relFile) => relFile.id !== fileId);
    setFiles(remainingFiles);
  };

  const handleClick = () => {
    if (fileInputRef.current) {
      fileInputRef.current.click();
    }
  };

  return (
    <Box display='flex' flexDirection='column' flex='1'>
      <Box className={classes.uploadAddDevicesHeader}>
        <Typography
          variant="h3"
          color="textPrimary"
          className={classes.uploadAddDevicesHeaderTitle}
        >
          <span className={classes.activePageUnderlined}>
            {isSensor ? 'Agregar sensor' : 'Agregar nodo'}
          </span>
        </Typography>
        <IconButton onClick={handleCloseModal}>
          <CloseIcon />
        </IconButton>
      </Box>
      <Divider className={classes.divider} />
      {(isSensor)
        && <>
          <Box className={classes.textFieldsContainer}>
            <TextField
              label='Nombre (*)'
              variant='standard'
              fullWidth
              className={classes.singleLeftTextfield}
              value={deviceInfo.name}
              onChange={(e) => handleValueChange(e, 'name')}
              error={error.name !== ''}
              helperText={error.name !== '' && error.name}
            />
            <TextField
              label='Tipo (*)'
              variant='standard'
              select
              fullWidth
              value={deviceInfo.sensorType}
              onChange={(e) => handleValueChange(e, 'sensorType')}
              error={error.sensorType !== ''}
              helperText={error.sensorType !== '' && error.sensorType}
            >
              {sensorTypes.map((type: string) => (
                <MenuItem key={type} value={type}>
                  {type}
                </MenuItem>
              ))}
               <MenuItem value={'clima'}>
                Clima
              </MenuItem>
            </TextField>
          </Box>
          <Box className={classes.textFieldsContainer}>
            <TextField
              label='Marca (Opcional)'
              variant='standard'
              select
              className={classes.singleLeftTextfield}
              fullWidth
              value={deviceInfo.softlandBrand}
              onChange={(e) => handleValueChange(e, 'softlandBrand')}
            >
              {
                sensorModels.brands.map((brand) => (
                  <MenuItem key={brand} value={brand}>
                    {brand}
                  </MenuItem>
                ))
              }
            </TextField>
            <Autocomplete
              options={sensorModels.models}
              fullWidth
              getOptionLabel={(option) => option}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label='Modelo - nombre (Opcional)'
                  variant='standard'
                />
              )}
              value={deviceInfo.softlandModel}
              onChange={(_e, value) => handleValueChange(value || '', 'softlandModel')}
            />
          </Box>
          <Box className={classes.textFieldsContainer}>
            {deviceInfo.sensorType === 'clima' && (
              <TextField
                label='¿Es una Estación de Clima? (*)'
                variant='standard'
                select
                fullWidth
                className={classes.singleLeftTextfield}
                value={deviceInfo.isWeatherStation ? 'true' : 'false'}
                onChange={(e) => handleValueChange(e, 'isWeatherStation', e.target.value === 'true')}
              >
                <MenuItem value="true">
                  Sí
                </MenuItem>
                <MenuItem value="false">
                  No
                </MenuItem>
              </TextField>
            )}
            {deviceInfo.sensorType !== 'clima' && (
              <TextField
                label='Wiseconn ID (Formato: 1-[Sensor ID])'
                variant='standard'
                fullWidth
                className={classes.singleLeftTextfield}
                value={deviceInfo.wiseconnId}
                onChange={(e) => handleValueChange(e, 'wiseconnId')}
                error={error.wiseconnId !== ''}
                helperText={error.wiseconnId !== '' && error.wiseconnId}
              />
            )}
            <TextField
              label='Nodo (*)'
              select
              variant='standard'
              fullWidth
              error={error.nodeId !== ''}
              helperText={error.nodeId !== '' && error.nodeId}
              value={deviceInfo.nodeId}
              onChange={(e) => handleValueChange(e, 'nodeId')}
            >
              {nodes.map((node: any) => (
                <MenuItem key={node.id} value={node.id}>
                  {node.name}
                </MenuItem>
              ))}
            </TextField>
          </Box>
          <Box className={classes.textFieldsContainer}>
            <TextField
              label='Telemetría (*)'
              variant='standard'
              select
              fullWidth
              className={classes.singleLeftTextfield}
              value={deviceInfo.telemetryType || 'wiseconn'}
              onChange={(e) => handleValueChange(e, 'telemetryType')}
            >
              <MenuItem value="wiseconn">
                Wiseconn
              </MenuItem>
              <MenuItem value="sn">
                Helius
              </MenuItem>
            </TextField>
            {
              <TextField
                label={deviceInfo.sensorType === 'clima' ? 'Zone ID (*)' : 'Zone ID'}
                variant='standard'
                fullWidth
                value={deviceInfo.zoneId || ''}
                onChange={(e) => handleValueChange(e, 'zoneId')}
                error={error.zoneId !== ''}
                helperText={error.zoneId !== '' && error.zoneId}
              />
            }
          </Box>
          <Box className={classes.textFieldsContainer}>
            <TextField
              label='Comentario'
              variant='standard'
              fullWidth
              value={deviceInfo.comment}
              onChange={(e) => handleValueChange(e, 'comment')}
            />
          </Box>
        </>
      }
      {(!isSensor) && <>
        <Box className={classes.textFieldsContainer}>
          <TextField
            label='Nombre (*)'
            variant='standard'
            fullWidth
            className={classes.singleLeftTextfield}
            value={deviceInfo.name}
            onChange={(e) => handleValueChange(e, 'name')}
            error={error.name !== ''}
            helperText={error.name !== '' && error.name}
          />
          <TextField
            label='Tipo (*)'
            variant='standard'
            fullWidth
            value={deviceInfo.nodeType}
            onChange={(e) => handleValueChange(e, 'nodeType')}
            error={error.nodeType !== ''}
            helperText={error.nodeType !== '' && error.nodeType}
          />
        </Box>
        <Box className={classes.textFieldsContainer}>
          <TextField
            label='Marca (Opcional)'
            variant='standard'
            select
            fullWidth
            className={classes.singleLeftTextfield}
            value={deviceInfo.softlandBrand}
            onChange={(e) => handleValueChange(e, 'softlandBrand')}
          >
            {
              nodeModels.brands.map((brand) => (
                <MenuItem key={brand} value={brand}>
                  {brand}
                </MenuItem>
              ))
            }
          </TextField>
          <Autocomplete
            options={nodeModels.models}
            fullWidth
            getOptionLabel={(option) => option}
            renderInput={(params) => (
              <TextField
                {...params}
                label='Modelo - nombre (Opcional)'
                variant='standard'
              />
            )}
            value={deviceInfo.softlandModel}
            onChange={(_e, value) => handleValueChange(value || '', 'softlandModel')}
          />
        </Box>
        <Box className={classes.textFieldsContainer}>
          <Box className={classes.checkboxContainer}>
            <FormControlLabel
              control={<Checkbox
                checked={deviceInfo.isGatewayNode}
                onChange={(e) => handleValueChange(e, 'isGatewayNode')}
              />}
              label="Nodo Gateway" />
          </Box>
          <TextField
            label='MAC (Opcional)'
            variant='standard'
            fullWidth
            value={deviceInfo.mac}
            onChange={(e) => handleValueChange(e, 'mac')}
            error={error.mac !== ''}
            helperText={error.mac !== '' && error.mac}
          />
        </Box>
        <Box className={classes.textFieldsContainer}>
          <TextField
            label='Telemetría (*)'
            variant='standard'
            select
            className={classes.singleLeftTextfield}
            fullWidth
            value={deviceInfo.telemetryType || 'wiseconn'}
            onChange={(e) => handleValueChange(e, 'telemetryType')}
          >
            <MenuItem value="wiseconn">
              Wiseconn
            </MenuItem>
            <MenuItem value="sn">
              Helius
            </MenuItem>
          </TextField>
          <TextField
            label='Wiseconn ID'
            variant='standard'
            fullWidth
            value={deviceInfo.wiseconnId}
            onChange={(e) => handleValueChange(e, 'wiseconnId')}
            error={error.wiseconnId !== ''}
            helperText={error.wiseconnId !== '' && error.wiseconnId}
          />
        </Box>
      </>}
      <Box role='button' onClick={handleClick} className={classes.uploadImagesContainer}>
        <SvgIcon sx={{ marginRight: '10px' }}>
          <UploadFileIcon />
        </SvgIcon>
        <Typography
          variant='body1'
        >
          SUBIR IMAGENES
        </Typography>
        <input
          ref={fileInputRef}
          type="file"
          multiple
          style={{ display: 'none' }}
          accept="image/*"
          name='files'
          onChange={handleFileChange}
        />
      </Box>
      <Box mt={1} mb={1}>
        {files && files.length > 0 && <>
          {files.map((relFile) => <Chip
            key={relFile.id}
            label={relFile.file.name}
            onDelete={() => handleDeleteFile(relFile.id)}
          />)}
        </>}
        {files && files.length === 0
          && <Typography mt={1} mb={1}>No hay imágenes seleccionadas</Typography>}
      </Box>
      <Box className={classes.buttonContainer}>
        <Box className={classes.singleButtonContainer} marginRight='20px'>
          <CustomButton buttonText='anterior' onClick={() => handleStep('firstStep')} />
        </Box>
        <Box className={classes.singleButtonContainer}>
          <CustomButton buttonText='siguiente' onClick={() => handleStep('thirdStep')} />
        </Box>
      </Box>
    </Box>
  );
};

export default AddDevicesFlowSecondStep;
